import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = ({ onOpenEnquiryForm }) => {
  const [hoveredLink, setHoveredLink] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  const isSpecialPage =
    location.pathname === "/about" ||
    location.pathname === "/contact" ||
    location.pathname === "/terms" ||
    location.pathname === "/services";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", checkMobile);
    
    // Initial check
    checkMobile();
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const textColor = "#000000";

  const headerBackground =
    isSpecialPage || scrolled ? "rgba(255,255,255,0.98)" : "transparent";

  const styles = {
    header: {
      backgroundColor: headerBackground,
      color: textColor,
      padding: scrolled ? "6px 16px" : "10px 16px",
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: "all 0.25s ease",
      boxShadow:
        isSpecialPage || scrolled
          ? "0 2px 12px rgba(0, 0, 0, 0.08)"
          : "none",
      borderBottom:
        isSpecialPage || scrolled ? "1px solid #e5e7eb" : "none",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
    },
    titleContainer: {
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      zIndex: 1001,
    },
    title: {
      fontSize: scrolled ? "1.4rem" : "1.6rem",
      fontWeight: "700",
      margin: 0,
      color: textColor,
      fontFamily: "'Georgia', serif",
      transition: "0.25s",
      lineHeight: '1.2',
    },
    subtitle: {
      fontSize: scrolled ? "0.7rem" : "0.8rem",
      marginTop: "2px",
      color: "#444",
      fontStyle: "italic",
      transition: "0.25s",
      whiteSpace: "nowrap",
    },
    desktopNav: {
      display: "flex",
      gap: "22px",
      alignItems: "center",
    },
    mobileNav: {
      display: "flex",
      flexDirection: "column",
      gap: "15px",
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      backgroundColor: "rgba(255,255,255,0.98)",
      padding: "20px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      borderTop: "1px solid #e5e7eb",
      zIndex: 999,
    },
    navLink: {
      color: textColor,
      textDecoration: "none",
      fontWeight: "500",
      fontSize: scrolled ? "0.85rem" : "0.95rem",
      transition: "0.2s",
      position: "relative",
      padding: "4px 0",
      cursor: "pointer",
    },
    enquiryButton: {
      backgroundColor: "#00ba76",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "5px",
      fontWeight: "500",
      fontSize: scrolled ? "0.85rem" : "0.95rem",
      cursor: "pointer",
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
    },
    navLinkHover: { color: "#2c5530" },
    underline: {
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      height: "2px",
      backgroundColor: "#2c5530",
      transform: "scaleX(0)",
      transition: "0.3s ease",
    },
    mobileMenuButton: {
      display: "none",
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      cursor: "pointer",
      color: textColor,
      padding: "8px",
      zIndex: 1001,
      minWidth: '44px',
      minHeight: '44px',
    },
  };

  const navItems = [
    { text: "Home", path: "/" },
    { text: "About Us", path: "/about" },
    { text: "Services", path: "/services" },
    { text: "Contact Us", path: "/contact" },
  ];

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.header')) {
        setIsMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <header style={styles.header} className="header">
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title} className="header-title">Dietitian Karpagam</h1>
          <p style={styles.subtitle} className="header-subtitle">Redefining wellness through food science</p>
        </div>

        {/* Desktop Navigation - Only show on desktop */}
        {!isMobile && (
          <nav style={styles.desktopNav} className="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.text}
                to={item.path}
                style={{
                  ...styles.navLink,
                  ...(hoveredLink === item.text ? styles.navLinkHover : {}),
                }}
                onMouseEnter={() => setHoveredLink(item.text)}
                onMouseLeave={() => setHoveredLink(null)}
              >
                {item.text}
                <span
                  style={{
                    ...styles.underline,
                    transform:
                      hoveredLink === item.text ? "scaleX(1)" : "scaleX(0)",
                  }}
                />
              </Link>
            ))}
            <button 
              style={styles.enquiryButton}
              className="enquiry-button"
              onClick={onOpenEnquiryForm}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#009a63';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#00ba76';
              }}
            >
              Enquiry Form
            </button>
          </nav>
        )}

        {/* Mobile Menu Button - Only show on mobile */}
        {isMobile && (
          <button 
            style={{...styles.mobileMenuButton, display: "block"}}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "✕" : "☰"}
          </button>
        )}

        {/* Mobile Navigation */}
        {isMobile && isMobileMenuOpen && (
          <div style={styles.mobileNav} className="mobile-nav">
            {navItems.map((item) => (
              <Link
                key={item.text}
                to={item.path}
                style={styles.navLink}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.text}
              </Link>
            ))}
            <button 
              style={styles.enquiryButton}
              onClick={() => {
                onOpenEnquiryForm();
                setIsMobileMenuOpen(false);
              }}
            >
              Enquiry Form
            </button>
          </div>
        )}
      </div>

      {/* Responsive CSS */}
      <style>
        {`
          /* Default Desktop Styles */
          @media (min-width: 1025px) {
            .mobile-menu-button,
            .mobile-nav {
              display: none !important;
            }
            .desktop-nav {
              display: flex !important;
            }
          }
          
          /* Mobile & Tablet Styles (up to 1024px) */
          @media (max-width: 1024px) {
            .desktop-nav {
              display: none !important;
            }
            .mobile-menu-button {
              display: block !important;
            }
          }
          
          /* iPad Pro, Surface Pro 7 (1024px - 1366px) */
          @media (max-width: 1024px) and (min-width: 769px) {
            .header {
              padding: 10px 24px !important;
            }
            .header-title {
              font-size: 1.5rem !important;
            }
            .header-subtitle {
              font-size: 0.8rem !important;
            }
          }
          
          /* iPad Mini, iPad Air, Surface Duo (768px - 820px) */
          @media (max-width: 820px) and (min-width: 540px) {
            .header {
              padding: 10px 20px !important;
            }
            .header-title {
              font-size: 1.4rem !important;
            }
            .header-subtitle {
              font-size: 0.75rem !important;
              max-width: 250px;
            }
          }
          
          /* iPhone 14 Pro Max, Pixel 7 (430px - 480px) */
          @media (max-width: 480px) and (min-width: 430px) {
            .mobile-menu-button {
              font-size: 1.4rem !important;
            }
            .header {
              padding: 8px 16px !important;
            }
            .header-title {
              font-size: 1.3rem !important;
              line-height: 1.1;
            }
            .header-subtitle {
              font-size: 0.7rem !important;
              max-width: 220px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
          
          /* iPhone 12 Pro, iPhone XR, Samsung Galaxy S20 Ultra (390px - 430px) */
          @media (max-width: 430px) and (min-width: 390px) {
            .mobile-menu-button {
              font-size: 1.4rem !important;
            }
            .header {
              padding: 8px 14px !important;
            }
            .header-title {
              font-size: 1.25rem !important;
              line-height: 1.1;
            }
            .header-subtitle {
              font-size: 0.68rem !important;
              max-width: 200px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
          
          /* iPhone SE, Samsung Galaxy A51/71 (375px - 390px) */
          @media (max-width: 390px) and (min-width: 375px) {
            .mobile-menu-button {
              font-size: 1.3rem !important;
            }
            .header {
              padding: 6px 12px !important;
            }
            .header-title {
              font-size: 1.2rem !important;
              line-height: 1.1;
            }
            .header-subtitle {
              font-size: 0.65rem !important;
              max-width: 180px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-top: 1px !important;
            }
          }
          
          /* Samsung Galaxy S8+, Pixel 7 (412px - 420px) */
          @media (max-width: 420px) and (min-width: 412px) {
            .header {
              padding: 7px 15px !important;
            }
            .header-title {
              font-size: 1.25rem !important;
            }
            .header-subtitle {
              font-size: 0.67rem !important;
              max-width: 190px;
            }
          }
          
          /* Very Small Devices (below 375px) */
          @media (max-width: 374px) {
            .mobile-menu-button {
              font-size: 1.2rem !important;
            }
            .header {
              padding: 5px 10px !important;
            }
            .header-title {
              font-size: 1.1rem !important;
              line-height: 1;
            }
            .header-subtitle {
              font-size: 0.6rem !important;
              max-width: 150px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-top: 0 !important;
            }
          }
          
          /* Foldable Devices (Galaxy Z Fold 5, Asus Zenbook Fold) */
          @media (max-width: 768px) and (min-height: 600px) and (max-height: 800px) {
            .header {
              padding: 10px 20px !important;
            }
            .header-title {
              font-size: 1.3rem !important;
            }
            .header-subtitle {
              font-size: 0.7rem !important;
              max-width: 220px;
            }
          }
          
          /* Nest Hub (1024px x 600px) */
          @media (max-width: 1024px) and (max-height: 600px) {
            .header {
              padding: 6px 16px !important;
            }
            .header-title {
              font-size: 1.2rem !important;
            }
            .header-subtitle {
              font-size: 0.65rem !important;
            }
          }
          
          /* Nest Hub Max (1280px x 800px) */
          @media (max-width: 1280px) and (max-height: 800px) {
            .header {
              padding: 8px 18px !important;
            }
            .header-title {
              font-size: 1.3rem !important;
            }
          }
          
          /* Landscape Mode for Mobile */
          @media (max-width: 896px) and (orientation: landscape) {
            .header {
              padding: 6px 12px !important;
            }
            .header-title {
              font-size: 1.1rem !important;
            }
            .header-subtitle {
              font-size: 0.6rem !important;
              max-width: 160px;
            }
            .mobile-nav {
              position: fixed !important;
              top: 60px !important;
              height: calc(100vh - 60px) !important;
              overflow-y: auto !important;
            }
          }
          
          /* Large Desktop Screens */
          @media (min-width: 1440px) {
            .header-title {
              font-size: 1.8rem !important;
            }
            .header-subtitle {
              font-size: 0.9rem !important;
            }
          }
          
          /* Touch-friendly button sizes */
          .mobile-menu-button,
          .enquiry-button,
          .mobile-nav .navLink {
            min-height: 44px !important;
            min-width: 44px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          /* Prevent body scroll when mobile menu is open */
          body.menu-open {
            overflow: hidden !important;
          }
          
          /* Mobile menu animations */
          .mobile-nav {
            animation: slideDown 0.3s ease-out !important;
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Desktop hover effects */
          .desktop-nav .navLink:hover {
            color: #2c5530 !important;
          }
          
          .desktop-nav .navLink:hover .underline {
            transform: scaleX(1) !important;
          }
          
          .enquiry-button:hover {
            background-color: #009a63 !important;
            transform: translateY(-1px);
          }
        `}
      </style>
    </header>
  );
};

export default Header;
